import _ from 'lodash';

/**
 * Tradução entre o recebedor que o backend devolve (`RecebedorPainel`) e o
 * formulário de conta digital, e do formulário para o payload interno que o
 * backend valida e envia à Pagar.me (Core API v5).
 *
 * Funções puras, sem redux e sem rede, para o mesmo formato valer na criação,
 * na edição e no preenchimento pela busca por CPF.
 */

export const TIPOS_CONTA = [
  { label: 'Conta Corrente', value: 'conta_corrente' },
  { label: 'Conta Poupança', value: 'conta_poupanca' },
];

export const UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
].map((sigla) => ({ label: sigla, value: sigla }));

export const TIPOS_EMPRESA = ['MEI', 'EI', 'LTDA', 'SLU', 'SA', 'Outros'].map((tipo) => ({
  label: tipo,
  value: tipo,
}));

export const ROTULOS_SITUACAO = {
  registration: 'Em cadastro: vende, mas ainda não saca',
  affiliation: 'Em credenciamento: vende, mas ainda não saca',
  active: 'Ativa',
  refused: 'Recusada',
  suspended: 'Suspensa',
  blocked: 'Bloqueada',
  inactive: 'Inativa',
};

export const ROTULOS_KYC = {
  approved: 'Aprovado',
  denied: 'Reprovado',
  partially_denied: 'Prova de vida pendente',
};

export const rotuloKyc = (status) => ROTULOS_KYC[status] || 'Em análise';

// Agência e conta como texto: `type="number"` apagava zeros à esquerda.
export const normalizarAgencia = (valor) =>
  String(valor ?? '')
    .replace(/\D/g, '')
    .slice(0, 4);

export const normalizarConta = (valor) =>
  String(valor ?? '')
    .replace(/\D/g, '')
    .slice(0, 13);

export const normalizarDigito = (valor) =>
  String(valor ?? '')
    .toUpperCase()
    .replace(/[^0-9X]/g, '')
    .slice(0, 2);

const enderecoParaFormulario = (endereco) =>
  endereco
    ? {
        zipCode: endereco.zip_code,
        street: endereco.street,
        streetNumber: endereco.street_number,
        complementary: endereco.complementary,
        neighborhood: endereco.neighborhood,
        city: endereco.city,
        state: endereco.state,
        referencePoint: endereco.reference_point,
      }
    : undefined;

const enderecoParaPayload = (endereco) => ({
  zipCode: endereco?.zipCode,
  street: endereco?.street,
  streetNumber: endereco?.streetNumber,
  complementary: endereco?.complementary,
  neighborhood: endereco?.neighborhood,
  city: endereco?.city,
  state: endereco?.state,
  referencePoint: endereco?.referencePoint,
});

const telefoneParaFormulario = (telefones) => ({
  ddd: telefones?.[0]?.ddd,
  number: telefones?.[0]?.number,
});

/**
 * `RecebedorPainel` → valores do formulário e resumo da situação na Pagar.me.
 * `registroTravado`: os dados vieram da Pagar.me, e a v5 não deixa editar
 * campo já preenchido.
 */
export function recebedorParaFormulario(
  resposta,
  { email, prefixo = 'create_account', campoSocios = 'managingPartners' } = {}
) {
  const ri = resposta?.register_information || {};
  const conta = resposta?.bank_account || {};
  const pj = ri.type === 'corporation' || conta.document_type === 'cnpj';
  const registroTravado = resposta?.register_information_source === 'pagarme';

  const formulario = {
    [prefixo]: {
      // Com o cadastro vindo da Pagar.me, o e-mail do usuário logado não entra
      // como sugestão: o campo seria travado com ele e enviado como e-mail do
      // recebedor, que a v5 não deixa corrigir depois.
      email: ri.email || (registroTravado ? undefined : email),
      businessUrl: ri.site_url,
      phone: telefoneParaFormulario(ri.phone_numbers),
      bankAccountType: pj ? 'pj' : 'pf',
      bankAccount: {
        bankNumber: conta.bank_code,
        agencyNumber: conta.agencia,
        agencyDv: conta.agencia_dv,
        accountNumber: conta.conta,
        accountDv: conta.conta_dv,
        accountType: conta.type,
        ownerName: pj ? undefined : ri.name || conta.holder_name || conta.legal_name,
        cpf: pj ? undefined : ri.document_number || ri.document || conta.document_number,
      },
      individual: {
        motherName: ri.mother_name,
        birthdate: ri.birthdate,
        monthlyIncome: ri.monthly_income,
        professionalOccupation: ri.professional_occupation,
        address: enderecoParaFormulario(ri.address),
      },
      corporation: {
        annualRevenue: ri.annual_revenue,
        corporationType: ri.corporation_type,
        foundingDate: ri.founding_date,
        mainAddress: enderecoParaFormulario(ri.main_address),
      },
    },
    [campoSocios]: (ri.managing_partners || []).map((socio) => ({
      name: socio?.name,
      documentNumber: socio?.document_number || socio?.document,
      email: socio?.email,
      motherName: socio?.mother_name,
      birthdate: socio?.birthdate,
      monthlyIncome: socio?.monthly_income,
      professionalOccupation: socio?.professional_occupation,
      selfDeclaredLegalRepresentative: socio?.self_declared_legal_representative,
      phone: telefoneParaFormulario(socio?.phone_numbers),
      address: enderecoParaFormulario(socio?.address),
    })),
  };

  return {
    formulario,
    recebedor: {
      id: resposta?.id,
      status: resposta?.status,
      statusLabel: resposta?.status_label || ROTULOS_SITUACAO[resposta?.status],
      kycStatus: resposta?.kyc?.status,
      kycStatusReason: resposta?.kyc?.status_reason,
      registroTravado,
    },
  };
}

/**
 * Valores do formulário → payload interno das rotas de conta digital. Nome,
 * documento e razão social da empresa vêm do estabelecimento no backend, por
 * isso não são enviados.
 */
export function formularioParaPayload(
  values,
  establishment,
  { prefixo = 'create_account', campoSocios = 'managingPartners' } = {}
) {
  const dados = values?.[prefixo] || {};
  const pj = dados.bankAccountType === 'pj';
  const conta = dados.bankAccount || {};

  const payload = {
    establishment_id: establishment?.id,
    bankAccountType: dados.bankAccountType,
    email: typeof dados.email === 'string' ? dados.email.trim() : dados.email,
    phone: { ddd: dados.phone?.ddd, number: dados.phone?.number },
    businessUrl: dados.businessUrl,
    bankAccount: {
      bankNumber: conta.bankNumber,
      agencyNumber: conta.agencyNumber,
      agencyDv: conta.agencyDv,
      accountNumber: conta.accountNumber,
      accountDv: conta.accountDv,
      accountType: conta.accountType,
      ...(pj ? {} : { ownerName: conta.ownerName, cpf: conta.cpf }),
    },
  };

  if (pj) {
    const empresa = dados.corporation || {};
    payload.corporation = {
      annualRevenue: empresa.annualRevenue,
      corporationType: empresa.corporationType,
      foundingDate: empresa.foundingDate,
      mainAddress: enderecoParaPayload(empresa.mainAddress),
    };
    // A Pagar.me aceita um único representante legal por empresa.
    payload.managingPartners = (values?.[campoSocios] || [])
      .filter(Boolean)
      .slice(0, 1)
      .map((socio) => ({
        name: socio.name,
        documentNumber: socio.documentNumber,
        email: socio.email,
        motherName: socio.motherName,
        birthdate: socio.birthdate,
        monthlyIncome: socio.monthlyIncome,
        professionalOccupation: socio.professionalOccupation,
        selfDeclaredLegalRepresentative: socio.selfDeclaredLegalRepresentative === true,
        phone: { ddd: socio.phone?.ddd, number: socio.phone?.number },
        address: enderecoParaPayload(socio.address),
      }));
  } else {
    const pessoa = dados.individual || {};
    payload.individual = {
      motherName: pessoa.motherName,
      birthdate: pessoa.birthdate,
      monthlyIncome: pessoa.monthlyIncome,
      professionalOccupation: pessoa.professionalOccupation,
      address: enderecoParaPayload(pessoa.address),
    };
  }

  return payload;
}

/** Campo já enviado à Pagar.me: só o suporte corrige. */
export function campoTravado(initialValues, caminho, recebedor) {
  if (!recebedor?.registroTravado) return false;
  const valor = _.get(initialValues, caminho);
  if (_.isNumber(valor)) return true;
  if (_.isBoolean(valor)) return valor;
  if (typeof valor === 'string') return valor.trim() !== '';
  return !_.isEmpty(valor);
}

/**
 * Props `disabled` e `validate` de um campo que pode vir travado. Campo
 * travado não é validado: o usuário não consegue corrigi-lo, o backend não
 * reenvia campo já preenchido na Pagar.me e valida de novo o que for enviar.
 * Uma regra do front mais estrita que a da Pagar.me (e-mail com terminação
 * longa, data em formato inesperado) bloquearia o envio inteiro, inclusive a
 * troca de conta bancária. `regras` precisa ser uma referência estável: o
 * redux-form registra o campo de novo quando `validate` muda.
 */
export function propsDeCampoTravavel(travado, regras) {
  return travado ? { disabled: true, validate: undefined } : { disabled: false, validate: regras };
}

/**
 * Guarda de corrida entre a busca de recebedor por CPF (com debounce e
 * assíncrona) e a edição de uma conta já cadastrada. Sem ela, a busca que o
 * próprio CPF da conta editada dispara, ou uma resposta que chega atrasada,
 * reinicializava o formulário no meio da edição e destravava os campos já
 * enviados à Pagar.me.
 *
 * Cada busca e cada pedido de edição recebem uma ficha; resposta com ficha
 * vencida é descartada.
 */
export function criarGuardaDaBusca() {
  let busca = 0;
  let pedidoDeEdicao = 0;
  let editando = false;

  return {
    /** Ficha da busca, ou null quando há conta em edição (não buscar). */
    iniciarBusca() {
      if (editando) return null;
      busca += 1;
      return busca;
    },

    /** A resposta desta busca ainda pode ser aplicada ao formulário? */
    buscaVigente(ficha) {
      return !editando && ficha !== null && ficha === busca;
    },

    /** Antes de carregar o detalhe da conta a editar. */
    pedirEdicao() {
      pedidoDeEdicao += 1;
      return pedidoDeEdicao;
    },

    /**
     * Com o detalhe carregado e antes de inicializar o formulário. Só o último
     * pedido vale (dois cliques em editar, ou Cancelar durante o carregamento),
     * e a partir daqui nenhuma resposta de busca é aplicada.
     */
    confirmarEdicao(ficha) {
      if (ficha !== pedidoDeEdicao) return false;
      editando = true;
      busca += 1;
      return true;
    },

    /** Formulário fechado (Cancelar ou salvo): vence buscas e pedidos. */
    encerrar() {
      editando = false;
      busca += 1;
      pedidoDeEdicao += 1;
    },

    emEdicao() {
      return editando;
    },
  };
}
